import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { IconButton } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import dayjs from 'dayjs';
import { IMovement } from 'features/inventory/movements/types';
import { ProductWarehouseStatus } from 'features/inventory/products/constants';
import { printLabel } from 'features/zebra';
import _ from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { StatusOption } from 'shared/types/general';
import { baseCell, dateTimeCell, booleanCell, currencyCell, dateCell } from 'components/cells';
import sapStateCell from 'components/cells/sap-state-cell';
import {
  filterOperators,
  numberFilterOperators,
  dateFilterOperators,
  booleanFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { getStatusKey } from 'components/helpers/dateUtils';

export function MovementsColumns(
  types: string[],
  canValidate = false,
  canEdit = false,
  detail = false,
  setMovement?: Dispatch<SetStateAction<number>>,
  editableExpiration = false,
  sap = false,
  printButton = true,
  productTypeOptions?: Array<{ value: string; label: string }>,
  labelOptions?: Array<{ value: string; label: string }>
): GridColDef[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translationPrefix = 'entities.movement';
  const isTenantAdminBottom = useIsBottomFromTenantAdmin();
  const isTech = useIsTechnician();

  const statusOptions: StatusOption[] = Object.entries(ProductWarehouseStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );

  const movementsDetail = (params) => {
    const filteredParams = _.omitBy(
      params,
      (value) => value === null || value === undefined || value === ''
    );
    filteredParams['discounted'] = params['discounted'];
    navigate({
      pathname: '/tenant/movements/detail/',
      search: `${createSearchParams(filteredParams)}`
    });
  };

  const columns: GridColDef[] | any[] = [];

  // Access the TENANT field from the .env file
  const tenant = process.env.REACT_APP_TENANT;

  if (tenant === 'ROMA2') {
    columns.push({
      field: 'scanned_code',
      headerName: t(`${translationPrefix}.scanned_code`),
      type: 'text',
      filterOperators,
      width: 150,
      renderCell: baseCell
    });
  }

  if (printButton) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const movementRow = params.row;
        const actions = [];

        // Action: Print Labels
        const handlePrintLabels = (barcodesOnly = false) => {
          printLabel(
            movementRow['product_code'],
            movementRow['product_batch'],
            movementRow['expiration_date'],
            movementRow['description'],
            barcodesOnly
          );
        };

        // Action: Delete Movement (if allowed)
        // const handleDeleteMovement = () => {
        //   if (setMovement && movementRow.id) {
        //     setMovement(+movementRow.id);
        //   }
        // };

        // CASE 1: Detail View
        if (detail) {
          if (movementRow.id) {
            actions.push(
              <IconButton size="small" onClick={() => handlePrintLabels(false)} color="primary">
                <LocalPrintshopIcon fontSize="small" /> {t('pages.movements.print_labels')}
              </IconButton>,
              <IconButton size="small" onClick={() => handlePrintLabels(true)} color="primary">
                <UnfoldLessIcon fontSize="small" /> {t('pages.movements.print_barcodes')}
              </IconButton>
            );

            // if (!movementRow.ddt_closed && isTenantAdminBottom) {
            //   actions.push(
            //     <IconButton size="small" onClick={handleDeleteMovement} color="error">
            //       <DeleteIcon fontSize="small" /> {t('global.delete')}
            //     </IconButton>
            //   );
            // }
          }
        }
        // CASE 2: List View
        else {
          actions.push({
            label: 'Dettaglio',
            icon: <ManageSearchIcon />,
            onClick: () =>
              movementsDetail({
                movement__id: movementRow.pk,
                product__id: movementRow.product__id,
                warehouse_code: movementRow.warehouse_code,
                type: movementRow.type,
                author: movementRow.author,
                product_batch: movementRow.product_batch,
                purchase_order: movementRow.purchase_order,
                timestamp__date: movementRow.timestamp.substring(0, 10),
                discounted: movementRow.discounted
              })
          });
        }

        return actions;
      },
      filterable: false
    } as GridColDef);
  }

  columns.push(
    {
      field: 'moved_quantity',
      filterOperators: numberFilterOperators,
      headerName: t(`${translationPrefix}.moved_quantity`),
      type: 'number',
      renderCell: (params: GridRenderCellParams<any>) => {
        const movement = params.row as IMovement;
        return (
          <>
            {movement.direction === 1 ? (
              <ArrowDownwardIcon color="success" />
            ) : (
              <ArrowUpward color="warning" />
            )}{' '}
            {movement.moved_quantity}
          </>
        );
      }
    },
    {
      field: 'product_code',
      headerName: t(`global.product`),
      filterOperators,
      width: 120,
      renderCell: baseCell
    },
    {
      field: 'product__type__code',
      headerName: t(`global.product_type`),
      filterable: true,
      sortable: true,
      width: 120,
      renderCell: baseCell,
      type: 'singleSelect',
      filterOperators: singleSelectFilterOperators,
      valueOptions: productTypeOptions || []
    },
    {
      field: 'category',
      headerName: t(`entities.product.category`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'protocollo',
      headerName: t(`entities.product.protocollo`),
      filterOperators,
      width: 120,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 100,
      type: 'singleSelect',
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: t(`entities.product.status_options.${opt.value}`)
        };
      }),
      filterOperators: singleSelectFilterOperators,
      valueFormatter: (params) => {
        const statusKey = getStatusKey(params.value);
        return statusKey ? t(`entities.product.status_options.${statusKey}`) : '';
      },
      renderCell: ({ value }) => (
        <span className={value === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
          {t(`entities.product.status_options.${value}`)}
        </span>
      )
    }
  );

  if (sap) {
    columns.push({
      field: 'state',
      headerName: t(`global.sap_state`),
      width: 80,
      renderCell: sapStateCell,
      filterable: false,
      sortable: false
    });
  }

  columns.push(
    {
      field: 'label',
      headerName: t(`global.label`),
      width: 150,
      renderCell: baseCell,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: labelOptions || [],
      filterOperators: singleSelectFilterOperators
    },
    {
      field: 'static_label',
      headerName: t(`${translationPrefix}.static_label`),
      width: 150,
      renderCell: baseCell,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: labelOptions || [],
      filterOperators: singleSelectFilterOperators
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      width: 300,
      renderCell: baseCell
    },
    {
      field: 'type_code',
      headerName: t(`${translationPrefix}.type`),
      type: 'singleSelect',
      valueOptions: types || [],
      width: 250,
      filterable: true,
      renderCell: baseCell,
      filterOperators: singleSelectFilterOperators
    },
    {
      field: 'timestamp',
      headerName: t(`${translationPrefix}.timestamp`),
      filterOperators: dateFilterOperators,
      type: 'date',
      width: 150,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateTimeCell
    },
    {
      field: 'discounted',
      headerName: t(`${translationPrefix}.discounted`),
      renderCell: booleanCell(t),
      filterOperators: booleanFilterOperators,
      width: 50
    }
  );
  if (!isTech) {
    columns.push(
      {
        field: 'price',
        headerName: t(`${translationPrefix}.price`),
        filterOperators: numberFilterOperators,
        type: 'number',
        sortable: false,
        width: 120,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value, { significantDigits: 3 });
        },
        renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
      },
      {
        field: 'static_unit_price', // usato solo come riferimento
        headerName: t(`${translationPrefix}.static_unit_price`),
        filterOperators: numberFilterOperators,
        type: 'number',
        sortable: false,
        width: 120,
        valueGetter: (params) =>
          params.row.static_unit_price ?? params.row.annotated_static_unit_price,

        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value, { significantDigits: 3 });
        },

        renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
      },
      {
        field: 'unit_price',
        headerName: t(`${translationPrefix}.unit_price`),
        filterOperators: numberFilterOperators,
        type: 'number',
        sortable: false,
        width: 120,
        valueFormatter: (params: GridValueFormatterParams) => {
          return currencyFormatter(params.value, { significantDigits: 3 });
        },
        renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
      }
    );
  }
  columns.push(
    {
      field: 'contract_code',
      headerName: t(`${translationPrefix}.contract_code`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'supplier',
      headerName: t(`global.supplier`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'static_contract_code',
      headerName: t(`${translationPrefix}.static_contract_code`),
      sortable: true,
      filterable: true,
      filterOperators,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'static_supplier',
      headerName: t(`${translationPrefix}.static_supplier`),
      sortable: true,
      filterable: true,
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'warehouse_code',
      headerName: t(`global.warehouse`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    {
      field: 'product_batch',
      headerName: t(`${translationPrefix}.product_batch`),
      filterOperators,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'expiration_date',
      headerName: t(`${translationPrefix}.expiration_date`),
      filterOperators: dateFilterOperators,
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      editable: editableExpiration,
      type: 'date',
      width: 100,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'note_location',
      headerName: t(`${translationPrefix}.note_location`),
      filterOperators,
      width: 250,
      renderCell: baseCell
    },
    // {
    //   field: 'notes',
    //   headerName: t(`global.notes`),
    //   width: 250,
    //   renderCell: baseCell,
    //   filterOperators
    // },
    {
      field: 'pw_notes',
      headerName: t(`global.notes`),
      width: 250,
      renderCell: baseCell,
      filterOperators
    },
    {
      field: 'order_code',
      headerName: t(`entities.movement.order_code`),
      filterOperators,
      editable: canEdit,
      width: 100,
      renderCell: baseCell
    },
    {
      field: 'order_date',
      headerName: t(`${translationPrefix}.order_date`),
      filterOperators: dateFilterOperators,
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      editable: canEdit,
      type: 'date',
      width: 100,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'billable',
      headerName: t(`entities.ddt.billable`),
      filterOperators: booleanFilterOperators,
      width: 100,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'invoice_ref',
      headerName: t(`entities.ddt.invoice_ref`),
      filterOperators,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'invoice_date',
      headerName: t(`entities.ddt.invoice_date`),
      filterOperators: dateFilterOperators,
      width: 200,
      renderCell: dateCell
    },
    {
      field: 'invoiced_amount',
      headerName: t(`${translationPrefix}.invoiced_amount`),
      type: 'number',
      width: 150,
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      valueFormatter: (params: GridValueFormatterParams) => {
        return currencyFormatter(params.value, { significantDigits: 3 });
      },
      renderCell: (params: GridRenderCellParams) => currencyCell(params, 3)
    },
    {
      field: 'credit_memo_ref',
      headerName: t(`entities.ddt.credit_memo_ref`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'credit_memo_date',
      headerName: t(`entities.ddt.credit_memo_date`),
      filterable: true,
      filterOperators: dateFilterOperators,
      sortable: true,
      type: 'date',
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      width: 200,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'credit_memo_amount',
      headerName: t(`${translationPrefix}.credit_memo_amount`),
      filterable: true,
      filterOperators: numberFilterOperators,
      sortable: true,
      type: 'number',
      width: 200,
      renderCell: currencyCell
    },
    {
      field: 'transport_document',
      valueGetter: (params) => params.row.transport_document_ref,
      headerName: t(`${translationPrefix}.transport_document`),
      filterOperators,
      width: 100,
      editable: canEdit,
      renderCell: baseCell
    },
    {
      field: 'transport_document_date',
      headerName: t(`${translationPrefix}.transport_document_date`),
      width: 100,
      type: 'date',
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'transport_document_notes',
      headerName: t(`${translationPrefix}.transport_document_notes`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    },
    {
      field: 'transport_document_closed',
      headerName: t(`${translationPrefix}.transport_document_closed`),
      filterable: true,
      filterOperators: booleanFilterOperators,
      sortable: true,
      width: 150,
      type: 'boolean',
      renderCell: booleanCell(t)
    },
    {
      field: 'transport_document_date_arrival',
      headerName: t(`${translationPrefix}.transport_document_date_arrival`),
      filterable: true,
      filterOperators: dateFilterOperators,
      sortable: true,
      type: 'date',
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      width: 150,
      valueGetter: ({ value }) => (value ? new Date(value) : null),
      renderCell: dateCell
    },
    {
      field: 'author',
      headerName: t(`${translationPrefix}.author`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 120,
      renderCell: baseCell
    },
    {
      field: 'approved_user',
      headerName: t(`${translationPrefix}.approved_user`),
      //filterOperators,
      filterable: false,
      sortable: false,
      width: 120,
      renderCell: baseCell
    },
    {
      field: 'approvation_date',
      headerName: t(`${translationPrefix}.approvation_date`),
      filterable: false,
      sortable: false,
      width: 120,
      renderCell: dateCell
    },
    {
      field: 'scanned_code',
      headerName: t(`${translationPrefix}.scanned_code`),
      filterable: true,
      filterOperators,
      sortable: true,
      width: 200,
      renderCell: baseCell
    }
  );

  return columns;
}
